/**
 * Thin typed wrapper around the RESTIFY control-plane API.
 *
 * Tokens are kept in localStorage so the dashboard reload doesn't drop the
 * session. The `BASE` env var is rewired by the Vite proxy in dev (`/api`
 * goes to the local Lambda-equivalent uvicorn).
 */

const TOKEN_KEY = "restify.token";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

export type OrderItem = {
  id: string;
  name: string;
  quantity: number;
  unit_price_minor: number;
  total_price_minor: number;
  notes: string | null;
  modifiers: Array<{
    name: string;
    quantity: number;
    price_minor: number;
  }>;
};

export type Order = {
  id: string;
  restaurant_id: string;
  platform: string;
  external_order_id: string;
  display_code: string | null;
  is_sandbox: boolean;
  status: string;
  order_type: string;
  placed_at: string;
  currency: string;
  totals: {
    subtotal_minor: number;
    delivery_fee_minor: number | null;
    service_fee_minor: number | null;
    tip_minor: number | null;
    discount_minor: number | null;
    total_minor: number;
  };
  customer: {
    first_name?: string;
    last_name?: string;
    name?: string;
    phone?: string;
    email?: string;
  } | null;
  items: OrderItem[];
  customer_notes: string | null;
  acknowledged_at: string | null;
  refunded_at: string | null;
  refund_amount_minor: number | null;
  disputed_at: string | null;
  dispute_reason: string | null;
};

export type Membership = { restaurant_id: string; role: string };

export type RestaurantMember = {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  joined_at: string;
};

export type Invitation = {
  id: string;
  restaurant_id: string;
  email: string;
  role: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
};

export type InvitationCreated = Invitation & { token: string };
export type Me = {
  id: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_superuser: boolean;
  preferred_language: string;
  memberships: Membership[];
};

export type BillingSubscription = {
  restaurant_id: string;
  plan: string;
  status: string;
  trial_ends_at: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  provider: string | null;
};

export type BillingEntitlements = {
  plan: string;
  seats: number | null;
  integrations: number | null;
  analytics: boolean;
  menu_sync: boolean;
};

export type BillingSnapshot = {
  subscription: BillingSubscription;
  entitlements: BillingEntitlements;
  trial_days_left: number | null;
  trial_expired: boolean;
  checkout_available: boolean;
};

export type Printer = {
  id: string;
  restaurant_id: string;
  name: string;
  ip: string;
  port: number;
  brand: string | null;
  is_active: boolean;
  auto_print_new: boolean;
  auto_print_accepted: boolean;
  auto_print_rejected: boolean;
};

export type DailyMetric = {
  day: string;
  platform: string | null;
  orders_count: number;
  cancelled_count: number;
  revenue_minor: number;
  avg_total_minor: number;
};

export type ReconciliationRow = {
  platform: string;
  orders_count: number;
  revenue_minor: number;
  delivery_fee_minor: number;
  service_fee_minor: number;
  tip_minor: number;
  discount_minor: number;
  net_minor: number;
};

export type RefundRecord = {
  id: string;
  order_id: string;
  restaurant_id: string;
  platform: string;
  external_refund_id: string | null;
  status: string;
  reason: string;
  reason_details: string | null;
  currency: string;
  requested_amount_minor: number;
  approved_amount_minor: number | null;
  requested_at: string;
  resolved_at: string | null;
  provider_response: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type HourlyMetric = {
  hour: number;
  orders_count: number;
  revenue_minor: number;
};

export type TopItem = {
  name: string;
  total_quantity: number;
  total_revenue_minor: number;
};

export type PlatformAvgCheck = {
  platform: string;
  orders_count: number;
  revenue_minor: number;
  avg_check_minor: number;
};

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

async function errorDetail(res: Response): Promise<string> {
  let detail = `HTTP ${res.status}`;
  try {
    const body = await res.json();
    if (typeof body?.detail === "string") {
      detail = body.detail;
    } else if (Array.isArray(body?.detail)) {
      detail = body.detail.map((d: any) => d.msg ?? JSON.stringify(d)).join("; ");
    } else if (body?.error?.message) {
      detail = body.error.message;
    }
  } catch {
    /* ignore */
  }
  return detail;
}

function authHeaders(): Headers {
  const headers = new Headers();
  headers.set("Content-Type", "application/json");
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return headers;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = authHeaders();
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    throw new Error(await errorDetail(res));
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/**
 * Authenticated file download. The JWT must stay in the Authorization header —
 * a query-string token is ignored by the API and would leak into access logs.
 */
async function downloadFile(
  path: string,
  filename: string,
): Promise<void> {
  const headers = new Headers();
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`${BASE}${path}`, { headers });
  if (!res.ok) {
    throw new Error(await errorDetail(res));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const api = {
  login: (email: string, password: string) =>
    request<{ access_token: string; expires_in: number }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  forgotPassword: (email: string) =>
    request<{ status: string }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  resetPassword: (token: string, newPassword: string) =>
    request<void>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, new_password: newPassword }),
    }),
  signup: (
    email: string,
    password: string,
    fullName: string,
    restaurantDetails?: {
      name: string;
      legal_name: string;
      tax_id: string;
      address: string;
      phone: string;
    },
    invitationToken?: string,
  ) =>
    request<{ access_token: string; expires_in: number }>("/auth/signup", {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        full_name: fullName,
        ...(invitationToken ? { invitation_token: invitationToken } : {}),
        ...(restaurantDetails
          ? {
              restaurant_name: restaurantDetails.name,
              legal_name: restaurantDetails.legal_name,
              tax_id: restaurantDetails.tax_id,
              address: restaurantDetails.address,
              phone: restaurantDetails.phone,
            }
          : {}),
      }),
    }),
  me: () => request<Me>("/auth/me"),
  billing: (restaurantId: string) =>
    request<BillingSnapshot>(`/restaurants/${restaurantId}/billing`),
  checkoutBilling: (restaurantId: string, plan: string) =>
    request<{ url: string }>(`/restaurants/${restaurantId}/billing/checkout`, {
      method: "POST",
      body: JSON.stringify({ plan }),
    }),
  listOrders: (restaurantId: string) =>
    request<Order[]>(`/orders?restaurant_id=${restaurantId}&limit=50`),
  getOrder: (orderId: string) =>
    request<Order>(`/orders/${orderId}`),
  printOrder: (orderId: string) =>
    request<{ status: string; message: string; results?: any }>(`/printers/print/${orderId}`, {
      method: "POST",
    }),
  transition: (orderId: string, status: string) =>
    request<Order>(`/orders/${orderId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status, push_to_provider: true }),
    }),
  acknowledge: (orderId: string) =>
    request<Order>(`/orders/${orderId}/acknowledge`, { method: "POST" }),
  refund: (orderId: string, amountMinor: number) =>
    request<Order>(`/orders/${orderId}/refund`, {
      method: "POST",
      body: JSON.stringify({ amount_minor: amountMinor }),
    }),
  dispute: (orderId: string, reason: string) =>
    request<Order>(`/orders/${orderId}/dispute`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  daily: (restaurantId: string) =>
    request<DailyMetric[]>(
      `/restaurants/${restaurantId}/analytics/daily?days=14`,
    ),
  reconciliation: (restaurantId: string, days = 30) =>
    request<ReconciliationRow[]>(
      `/restaurants/${restaurantId}/analytics/reconciliation?days=${days}`,
    ),
  hourlyMetrics: (restaurantId: string, days = 30) =>
    request<HourlyMetric[]>(
      `/restaurants/${restaurantId}/analytics/hourly?days=${days}`,
    ),
  topItems: (restaurantId: string, days = 30, limit = 10) =>
    request<TopItem[]>(
      `/restaurants/${restaurantId}/analytics/top-items?days=${days}&limit=${limit}`,
    ),
  avgCheck: (restaurantId: string, days = 30) =>
    request<PlatformAvgCheck[]>(
      `/restaurants/${restaurantId}/analytics/avg-check?days=${days}`,
    ),
  exportCsv: (restaurantId: string, days = 30) =>
    downloadFile(
      `/restaurants/${restaurantId}/analytics/export?days=${days}`,
      "orders_export.csv",
    ),
  listIntegrations: (restaurantId: string) =>
    request<any[]>(`/integrations?restaurant_id=${restaurantId}`),
  createIntegration: (data: any) =>
    request<any>("/integrations", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  getIntegrationReadiness: (integrationId: string) =>
    request<{
      ready_for_live_test: boolean;
      live_webhook_verified: boolean;
      checks: Record<string, boolean>;
      issues: Record<string, string>;
    }>(`/integrations/${integrationId}/readiness`),
  disconnectIntegration: (id: string) =>
    request<any>(`/integrations/${id}/disconnect`, {
      method: "POST",
    }),
  updateIntegration: (id: string, data: any) =>
    request<any>(`/integrations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteIntegration: (id: string) =>
    request<void>(`/integrations/${id}`, {
      method: "DELETE",
    }),
  createRestaurant: (data: {
    name: string;
    legal_name: string;
    tax_id: string;
    address: string;
    phone: string;
    contact_email: string;
  }) =>
    request<any>("/restaurants", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  listMenu: (restaurantId: string) =>
    request<any[]>(`/restaurants/${restaurantId}/menu`),
  createMenuItem: (restaurantId: string, data: any) =>
    request<any>(`/restaurants/${restaurantId}/menu`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateMenuItem: (itemId: string, data: any) =>
    request<any>(`/menu-items/${itemId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteMenuItem: (itemId: string) =>
    request<any>(`/menu-items/${itemId}`, { method: "DELETE" }),
  syncMenu: (restaurantId: string) =>
    request<any>(`/restaurants/${restaurantId}/menu/sync`),
  updateItemAvailability: (itemId: string, data: { availability: string; snoozed_until?: string }) =>
    request<any>(`/menu-items/${itemId}/availability`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  discoverPrinters: (restaurantId: string, subnet?: string) =>
    request<any>(`/printers/discover`, {
      method: "POST",
      body: JSON.stringify({ restaurant_id: restaurantId, subnet: subnet ?? "192.168.1" }),
    }),
  testPrinter: (restaurantId: string, ip: string, port?: number) =>
    request<any>(`/printers/test`, {
      method: "POST",
      body: JSON.stringify({ restaurant_id: restaurantId, ip, port: port ?? 9100 }),
    }),
  triggerSimulatedOrder: (integrationId: string, platform: string) =>
    request<any>(`/simulation/trigger-order?integration_id=${integrationId}&platform=${platform}`, {
      method: "POST",
    }),
  updateMe: (data: { full_name?: string; preferred_language?: string }) =>
    request<Me>("/auth/me", {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  listPrinters: (restaurantId: string) =>
    request<Printer[]>(`/printers?restaurant_id=${restaurantId}`),
  createPrinter: (restaurantId: string, data: Partial<Printer>) =>
    request<Printer>(`/printers?restaurant_id=${restaurantId}`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updatePrinter: (printerId: string, data: Partial<Printer>) =>
    request<Printer>(`/printers/${printerId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deletePrinter: (printerId: string) =>
    request<void>(`/printers/${printerId}`, {
      method: "DELETE",
    }),
  getWsTicket: (restaurantId: string) =>
    request<{ ticket: string; expires_in: number }>(
      `/ws/ticket?restaurant_id=${restaurantId}`,
      { method: "POST" },
    ),
  getOnboardingStatus: (restaurantId: string) =>
    request<{
      restaurant_id: string;
      has_integrations: boolean;
      active_integrations_count: number;
      has_sandbox_integration: boolean;
      has_received_order: boolean;
      has_printer: boolean;
      completed: boolean;
    }>(`/restaurants/${restaurantId}/onboarding-status`),
  listRefunds: (restaurantId: string, status?: string) => {
    let url = `/refunds?restaurant_id=${restaurantId}`;
    if (status) url += `&status=${status}`;
    return request<RefundRecord[]>(url);
  },
  listRefundsForOrder: (orderId: string) =>
    request<RefundRecord[]>(`/refunds/order/${orderId}`),
  createRefund: (data: {
    order_id: string;
    reason: string;
    reason_details?: string;
    requested_amount_minor: number;
    currency?: string;
  }) =>
    request<RefundRecord>("/refunds", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  transitionRefund: (refundId: string, data: {
    status: string;
    approved_amount_minor?: number;
    provider_response?: Record<string, unknown>;
  }) =>
    request<RefundRecord>(`/refunds/${refundId}/status`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  listMembers: (restaurantId: string) =>
    request<RestaurantMember[]>(`/restaurants/${restaurantId}/members`),
  removeMember: (restaurantId: string, userId: string) =>
    request<void>(`/restaurants/${restaurantId}/members/${userId}`, {
      method: "DELETE",
    }),
  listInvitations: (restaurantId: string) =>
    request<Invitation[]>(`/restaurants/${restaurantId}/invitations`),
  createInvitation: (restaurantId: string, email: string, role: string) =>
    request<InvitationCreated>(`/restaurants/${restaurantId}/invitations`, {
      method: "POST",
      body: JSON.stringify({ email, role }),
    }),
  revokeInvitation: (restaurantId: string, invitationId: string) =>
    request<void>(`/restaurants/${restaurantId}/invitations/${invitationId}`, {
      method: "DELETE",
    }),
  acceptInvitation: (token: string) =>
    request<Membership>("/invitations/accept", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
};
